/**
 * Builds the billing-info patch for the dev-tools billing section.
 *
 * Tier and seat land on both `tier` and `new_product_tier` so free-user checks
 * (`tier`) and the evidence-launch tier (`new_product_tier`) agree. Trialing
 * sets the reverse-trial flag plus an end date, because the header countdown
 * reads the patched day/hour counters while clinician surfaces read
 * `evidence_billing.trial_end_date`.
 */
import { addHours, addMinutes } from 'date-fns';

import { BillingSeatType, SubscriptionTier } from '@heidi/schema/ml-scribe';

import type { BillingDevForm, BillingDevOverride, BillingDevTier } from './overrides';

export const DEFAULT_BILLING_DEV_FORM: BillingDevForm = {
  tier: 'free',
  seatType: BillingSeatType.STANDARD,
  trialing: false,
  trialDaysLeft: 14,
  trialHoursLeft: 0,
  trialAvailable: true,
};

export const BILLING_DEV_TIER_OPTIONS: { value: BillingDevTier; label: string }[] = [
  { value: 'free', label: 'Free' },
  { value: 'clinician', label: 'Clinician' },
  { value: 'practice', label: 'Practice' },
  { value: 'scribeTeam', label: 'Scribe Team (legacy)' },
];

export const BILLING_DEV_SEAT_OPTIONS: { value: BillingSeatType; label: string }[] = [
  { value: BillingSeatType.STANDARD, label: 'Standard' },
  { value: BillingSeatType.MAX, label: 'Max' },
];

const TIER_TO_SUBSCRIPTION: Record<BillingDevTier, SubscriptionTier> = {
  free: SubscriptionTier.FREE,
  clinician: SubscriptionTier.CLINICIAN_PRO,
  practice: SubscriptionTier.CLINICIAN_PRO_PLUS,
  scribeTeam: SubscriptionTier.PRO_PLUS,
};

const TIER_LABEL: Record<BillingDevTier, string> = {
  free: 'Free',
  clinician: 'Clinician',
  practice: 'Practice',
  scribeTeam: 'Scribe Team',
};

/** Empty when a clinician trial is still available; both clinician tiers once it isn't. */
export function trialHistoryTiersForAvailability(
  trialAvailable: boolean
): SubscriptionTier[] {
  return trialAvailable
    ? []
    : [SubscriptionTier.CLINICIAN_PRO, SubscriptionTier.CLINICIAN_PRO_PLUS];
}

export function buildBillingDevOverride(
  form: BillingDevForm,
  now = new Date()
): BillingDevOverride {
  const subscriptionTier = TIER_TO_SUBSCRIPTION[form.tier];
  const totalHours = form.trialDaysLeft * 24 + form.trialHoursLeft;
  // ponytail: the credit tile reads differenceInHours(end, now), which floors,
  // and switches to an hour count once that floor is under 24. An exact 24h
  // end is already 23 a second later, so 1 day 0 hours renders as 23. Pad 59m
  // so the floored hour keeps matching the inputs. Ceiling: in the last 59
  // minutes before local midnight, 1 day can render as 2 calendar days.
  const trialEnd = form.trialing
    ? addMinutes(addHours(now, totalHours), 59).toISOString()
    : null;
  const seat = form.seatType === BillingSeatType.MAX ? 'Max' : 'Standard';
  const tierLabel = TIER_LABEL[form.tier];
  const label = form.trialing
    ? `${tierLabel} · ${seat} · trial ${form.trialDaysLeft}d ${form.trialHoursLeft}h`
    : form.trialAvailable
      ? `${tierLabel} · ${seat} · trial available`
      : `${tierLabel} · ${seat}`;

  return {
    label,
    form,
    patch: {
      tier: subscriptionTier,
      new_product_tier: subscriptionTier,
      seat_type: form.seatType,
      third_party_license: null,
      is_in_free_trial: form.trialing,
      is_in_reverse_free_trial: form.trialing,
      is_reverse_trial_extendable: false,
      is_fake_clinician_trial: false,
      is_promoted_free_trial: false,
      previous_status_before_clinician_trial: null,
      trial_end_date: trialEnd,
      trialDaysLeft: form.trialing ? form.trialDaysLeft : 0,
      trialHoursLeft: form.trialing ? totalHours : 0,
      evidence_billing: {
        is_in_free_trial: form.trialing,
        trial_end_date: trialEnd,
      },
    },
  };
}
